/**
 * AI Scribe Recording Utilities
 *
 * Formatting and display helpers for recording data.
 */

import type { RecordingStatus } from './types';

// ─── Duration Formatting ────────────────────────────────────────────

/**
 * Format duration in seconds to a human-readable string.
 * @example formatDuration(2520) → "42 min"
 * @example formatDuration(4500) → "1h 15min"
 * @example formatDuration(45) → "< 1 min"
 */
export function formatDuration(seconds: number | null): string {
  if (!seconds || seconds <= 0) return '--';
  if (seconds < 60) return '< 1 min';

  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  }
  return `${mins} min`;
}

/**
 * Format elapsed seconds as HH:MM:SS for the recording timer.
 * @example formatTimer(2520) → "00:42:00"
 */
export function formatTimer(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return [h, m, s].map((v) => String(v).padStart(2, '0')).join(':');
}

// ─── Date Formatting ────────────────────────────────────────────────

/**
 * Format an ISO date string to "14 Mar 2026" format.
 */
export function formatRecordingDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

// ─── Status Config ──────────────────────────────────────────────────

interface StatusConfig {
  label: string;
  dotClass: string;
  badgeClass: string;
}

const STATUS_CONFIGS: Record<RecordingStatus, StatusConfig> = {
  draft: {
    label: 'Draft',
    dotClass: 'bg-slate-400',
    badgeClass: 'bg-slate-50 text-slate-600 ring-slate-200'
  },
  started: {
    label: 'Recording',
    dotClass: 'bg-blue-500',
    badgeClass: 'bg-blue-50 text-blue-700 ring-blue-200'
  },
  ready: {
    label: 'Ready for review',
    dotClass: 'bg-emerald-500',
    badgeClass: 'bg-emerald-50 text-emerald-700 ring-emerald-200'
  },
  uploading: {
    label: 'Uploading',
    dotClass: 'bg-blue-500',
    badgeClass: 'bg-blue-50 text-blue-700 ring-blue-200'
  },
  processing: {
    label: 'Processing',
    dotClass: 'bg-blue-500',
    badgeClass: 'bg-blue-50 text-blue-700 ring-blue-200'
  },
  imported: {
    label: 'Imported',
    dotClass: 'bg-gray-400',
    badgeClass: 'bg-gray-100 text-gray-600 ring-gray-200'
  },
  dismissed: {
    label: 'Dismissed',
    dotClass: 'bg-gray-300',
    badgeClass: 'bg-gray-50 text-gray-500 ring-gray-200'
  },
  failed: {
    label: 'Processing failed',
    dotClass: 'bg-red-500',
    badgeClass: 'bg-red-50 text-red-700 ring-red-200'
  }
};

/**
 * Get display configuration for a recording status.
 */
export function getStatusConfig(status: RecordingStatus): StatusConfig {
  return STATUS_CONFIGS[status];
}
